import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { TRY, NUM, fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import HtmlPreviewDialog, { authUrl, fetchPreviewUrl, fetchDraftGibPreview, buildInvoiceHtml } from "@/components/HtmlPreviewDialog";
import { COMPANY } from "@/lib/company";
import { loadCompanyLogo } from "@/lib/branding";
import { Receipt, FileText, Eye, Zap, CheckCircle2, XCircle, Clock, Paperclip, ReceiptText, Inbox, Loader2, Tag, Percent } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useInvoiceView } from "@/lib/invoiceView";
import { toast } from "sonner";

const fmtDT = fmtDateTime;   // UTC ISO → Türkiye saati (Europe/Istanbul)
const PM = { havale: "Havale/EFT", nakit: "Nakit", kredi_karti: "Kredi Kartı", cek: "Çek" };
const Info = ({ label, value, mono }) => (
  <div className="min-w-0"><div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div><div className={`text-sm break-words ${mono ? "font-mono text-xs" : "font-medium"}`}>{value || "—"}</div></div>
);
const Section = ({ title, children, testid }) => (
  <div className="rounded-xl border p-4 space-y-3" data-testid={testid}><div className="text-xs font-black uppercase tracking-wider text-secondary">{title}</div>{children}</div>
);

// Fatura tam detay: cari, kalemler, toplamlar, ödeme/tahsilat, QNB durumu, belgeler
export default function InvoiceDetailDialog({ invoice, contacts = [], onClose, onQnb, onAddPayment }) {
  const [inv, setInv] = useState(invoice);
  const [receipts, setReceipts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useInvoiceView();
  const showCodes = view.include_codes === true;
  const showDisc = view.discount_display === "show";
  useEffect(() => {
    setInv(invoice); setReceipts([]);
    if (invoice?.id) api.get(`/receipts?invoice_id=${invoice.id}`).then((r) => setReceipts(r.data)).catch(() => {});
  }, [invoice]);
  if (!inv) return <Dialog open={false} />;

  const c = contacts.find((x) => x.title === inv.contact_title) || {};
  const q = inv.qnb || {};
  const st = q.status || {};
  const paid = (inv.payment_history || []).reduce((s, h) => s + Number(h.amount || 0), 0) || (inv.paid ? inv.total : 0);
  const remain = Math.max(0, (inv.total || 0) - paid);
  const payState = remain <= 0.005 ? "paid" : paid > 0 ? "partial" : "unpaid";
  const isSale = inv.type === "satis";
  const source = inv.source_type === "efatura_gelen" ? "QNB Gelen e-Fatura" : inv.type === "alis" ? "Manuel Giriş" : q.success ? q.channel_label : "Taslak (kesilmedi)";
  const lineDisc = (inv.items || []).reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0) * (Number(it.discount_rate || 0) / 100), 0);

  const openPreview = async () => {
    setBusy(true);
    try {
      if (q.ettn) {
        const url = await fetchPreviewUrl(`/invoices/${inv.id}/qnb-pdf`);
        setPreview({ url, title: inv.invoice_no || inv.contact_title });
        return;
      }
      if (isSale) {
        // Taslak satış faturası: GİB'de görüneceği şablonla (QNB ublOnizleme) önizle; alınamazsa yerel şablon
        try { setPreview({ ...(await fetchDraftGibPreview(inv, view)), regen: (v) => fetchDraftGibPreview(inv, v) }); return; }
        catch (e) { toast.warning(`GİB görünümü alınamadı — yerel şablon gösteriliyor · ${apiError(e)}`); }
      }
      const logo = await loadCompanyLogo();
      const local = (v) => ({ html: buildInvoiceHtml(inv, c, COMPANY, logo, v), title: inv.invoice_no || inv.contact_title });
      setPreview({ ...local(view), regen: async (v) => local(v) });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const openPdf = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/invoices/${inv.id}/qnb-pdf`); window.open(authUrl(data.url), "_blank"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="max-w-[96vw] sm:max-w-4xl max-h-[92vh] overflow-y-auto p-0 gap-0" data-testid="invoice-detail-dialog">
        <DialogHeader className="p-5 pb-4 bg-gradient-to-br from-secondary to-secondary/80 text-white rounded-t-lg">
          <DialogTitle className="font-head text-2xl flex items-center gap-2 text-white">
            <span className="h-9 w-9 rounded-xl bg-white/15 flex items-center justify-center"><Receipt className="h-5 w-5" /></span>
            {isSale ? "Satış Faturası" : "Alış Faturası"} Detayı
            <Badge className="ml-1 bg-white/20 text-white border-0" data-testid="detail-source-badge">{source}</Badge>
          </DialogTitle>
          <div className="mt-2 text-sm">
            <div className="font-semibold text-white">{inv.contact_title}</div>
            <div className="flex flex-wrap items-center gap-2 text-white/75 text-xs">
              <span className="font-mono">{inv.invoice_no || "Fatura no yok"}</span><span>•</span><span>{fmtDate(inv.date)}</span><span>•</span>
              <span className="tabular-nums font-bold text-white">{TRY(inv.total)}</span>
              {q.status_label && <Badge variant="outline" className="ml-auto border-white/40 text-white" data-testid="detail-qnb-status">{q.status_label}</Badge>}
            </div>
          </div>
        </DialogHeader>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title={isSale ? "Müşteri (Cari)" : "Tedarikçi (Cari)"} testid="detail-contact">
              <Info label="Ünvan" value={inv.contact_title} />
              <div className="grid grid-cols-2 gap-3">
                <Info label="VKN / TCKN" value={c.tax_no || q.receiver?.vkn} mono />
                <Info label="Telefon" value={c.phone} />
              </div>
              <Info label="Adres" value={c.address || q.receiver?.address} />
              <Info label="E-posta" value={c.email || q.receiver?.email} />
            </Section>
            <Section title="Fatura Bilgileri" testid="detail-info">
              <div className="grid grid-cols-2 gap-3">
                <Info label="Fatura No" value={inv.invoice_no} mono />
                <Info label="Tarih" value={fmtDate(inv.date)} />
                <Info label="Vade" value={inv.due_date ? fmtDate(inv.due_date) : "—"} />
                <Info label="Ödeme Yöntemi" value={PM[inv.payment_method] || inv.payment_method} />
                <Info label="Kaynak" value={source} />
                <Info label="Oluşturma" value={fmtDT(inv.created_at)} />
              </div>
              {inv.note && <Info label="Not" value={inv.note} />}
            </Section>
          </div>

          <div className="rounded-xl border overflow-hidden" data-testid="detail-lines">
            <div className="px-4 py-2 bg-muted/50 text-xs font-black uppercase tracking-wider flex items-center gap-3 flex-wrap">
              <span>Mal / Hizmet Kalemleri ({(inv.items || []).length})</span>
              <div className="ml-auto flex items-center gap-3 normal-case tracking-normal font-medium" data-testid="detail-view-toggles">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer"><Switch checked={showCodes} onCheckedChange={(v) => setView({ include_codes: v })} data-testid="detail-toggle-codes" /><Tag className="h-3.5 w-3.5 text-muted-foreground" />Ürün kodu</label>
                <div className="flex items-center gap-1.5 text-xs"><Percent className="h-3.5 w-3.5 text-muted-foreground" />
                  <Select value={showDisc ? "show" : "net"} onValueChange={(v) => setView({ discount_display: v })}>
                    <SelectTrigger className="h-7 w-44 text-xs bg-card" data-testid="detail-toggle-discount"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="net">İskonto gizli (net fiyat)</SelectItem><SelectItem value="show">Liste fiyatı + iskonto</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-8">#</TableHead>{showCodes && <TableHead>Kod</TableHead>}<TableHead>Ürün / Hizmet</TableHead><TableHead className="text-right">Miktar</TableHead><TableHead>Birim</TableHead>
                  {showDisc && <><TableHead className="text-right">Liste B.Fiyat</TableHead><TableHead className="text-right">İsk %</TableHead></>}<TableHead className="text-right">{showDisc ? "Net B.Fiyat" : "Birim Fiyat"}</TableHead><TableHead className="text-right">Tutar</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(inv.items || []).map((it, i) => {
                    const nu = Number(it.unit_price || 0) * (1 - Number(it.discount_rate || 0) / 100);
                    return (
                      <TableRow key={i} data-testid={`detail-line-${i}`}>
                        <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                        {showCodes && <TableCell className="font-mono text-xs">{it.code || "—"}</TableCell>}
                        <TableCell><div className="font-semibold">{it.name}</div>{it.description && <div className="text-xs text-muted-foreground">{it.description}</div>}</TableCell>
                        <TableCell className="text-right tabular-nums">{NUM(it.quantity)}</TableCell>
                        <TableCell className="text-xs uppercase">{it.unit || "ADET"}</TableCell>
                        {showDisc && <><TableCell className="text-right tabular-nums">{TRY(it.unit_price)}</TableCell>
                        <TableCell className="text-right tabular-nums">{Number(it.discount_rate) ? NUM(it.discount_rate) : "—"}</TableCell></>}
                        <TableCell className="text-right tabular-nums">{TRY(nu)}</TableCell>
                        <TableCell className="text-right tabular-nums font-bold">{TRY(Number(it.quantity || 0) * nu)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-4 bg-muted/30 text-sm" data-testid="detail-totals">
              <Info label="Liste Toplamı" value={TRY(inv.subtotal)} />
              <Info label={`İskonto${Number(inv.discount_rate) ? ` (satır + %${NUM(inv.discount_rate)} genel)` : lineDisc > 0 ? " (satır)" : ""}`} value={TRY(inv.discount)} />
              <Info label="Net (Matrah)" value={TRY(inv.net)} />
              <Info label={`KDV (%${NUM(inv.kdv_rate)})`} value={TRY(inv.kdv)} />
              <div><div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Genel Toplam</div><div className="font-head font-black text-lg text-secondary tabular-nums">{TRY(inv.total)}</div></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title={isSale ? "Tahsilat Durumu" : "Ödeme Durumu"} testid="detail-payment">
              <div className="flex items-center gap-2 flex-wrap">
                {payState === "paid" ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" />{isSale ? "Tahsil edildi" : "Ödendi"}</Badge>
                  : payState === "partial" ? <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700"><Clock className="h-3 w-3 mr-1" />{isSale ? "Kısmen tahsil edildi" : "Kısmen ödendi"}</Badge>
                  : <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />{isSale ? "Tahsil edilmedi" : "Ödenmedi"}</Badge>}
                <span className="text-xs text-muted-foreground tabular-nums">{isSale ? "Tahsil edilen" : "Ödenen"} {TRY(paid)} · Kalan <b className={remain > 0 ? "text-amber-700" : ""}>{TRY(remain)}</b></span>
                {onAddPayment && remain > 0.005 && <Button size="sm" variant="outline" className="h-7 ml-auto" onClick={() => onAddPayment(inv)} data-testid="detail-add-payment-btn"><Receipt className="h-3.5 w-3.5 mr-1" />{isSale ? "Tahsilat Ekle" : "Ödeme Ekle"}</Button>}
              </div>
              {(inv.payment_history || []).length > 0 && (
                <div className="text-xs space-y-1 border-t pt-2">
                  {(inv.payment_history || []).map((h) => <div key={h.id} className="flex justify-between"><span>{fmtDate(h.date)} · {PM[h.method] || h.method || ""}{h.note ? ` · ${h.note}` : ""}</span><span className="tabular-nums font-semibold">{TRY(h.amount)}</span></div>)}
                </div>
              )}
              <div className="border-t pt-2 space-y-1" data-testid="detail-receipts">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><ReceiptText className="h-3 w-3" />Tahsilat Makbuzu</div>
                {receipts.length === 0 ? <div className="text-xs text-muted-foreground">Bu faturaya bağlı tahsilat makbuzu kesilmedi.</div> : receipts.map((r) => {
                  const doc = (inv.documents || []).find((d) => (d.name || "").includes(r.receipt_no));
                  return (
                    <div key={r.id} className="flex items-center gap-2 text-xs" data-testid={`detail-receipt-${r.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      <span><b>Tahsilat makbuzu kesildi</b> · #{r.receipt_no} · {fmtDate(r.date)} · {PM[r.method] || r.method} · <span className="tabular-nums font-semibold">{TRY(r.amount)}</span>{r.received_from ? ` · ${r.received_from}` : ""} <span className="text-muted-foreground">(ödeme olarak işlendi)</span></span>
                      {doc && <button className="ml-auto underline text-secondary inline-flex items-center gap-1" onClick={() => setPreview({ url: doc.url, title: `Makbuz ${r.receipt_no}` })} data-testid={`detail-receipt-pdf-${r.id}`}><FileText className="h-3 w-3" />PDF</button>}
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section title="QNB e-Fatura / e-Arşiv Durumu" testid="detail-qnb">
              {q.success || q.ettn ? (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline">{q.channel_label}</Badge>{q.profile_label && <Badge variant="outline">{q.profile_label}</Badge>}{q.type_label && <Badge variant="outline">{q.type_label}</Badge>}
                    <Badge className={q.cancelled || st.durum === 2 ? "bg-red-600" : "bg-emerald-600"}>{q.status_label || (q.cancelled ? "İPTAL" : "Gönderildi")}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <Info label="Fatura No" value={q.invoice_no} mono /><Info label="ETTN" value={q.ettn} mono />
                    <Info label="Kesen" value={`${q.sent_by || "—"} · ${fmtDT(q.sent_at)}`} /><Info label="İskonto Gösterimi" value={q.discount_display === "show" ? "Liste fiyatı + iskonto" : "Net fiyat"} />
                    <Info label="Alıcı GİB Ünvanı" value={q.receiver_info?.unvan} /><Info label="Alıcı Etiketi" value={q.receiver_info?.etiket} mono />
                    {q.channel === "efatura" && <><Info label="QNB İşlem" value={st.durum_text} /><Info label="Gönderim" value={st.gonderim_durumu_text} /><Info label="GİB Yanıtı" value={st.gib_detay || (st.gib_kodu != null ? String(st.gib_kodu) : "")} /><Info label="Alıcı Yanıtı" value={`${st.yanit_durumu_text || "—"}${st.yanit_tarihi ? " · " + fmtDT(st.yanit_tarihi) : ""}`} /></>}
                    {q.cancelled && <div className="col-span-2 text-red-700">İptal: {fmtDT(q.cancelled_at)} · {q.cancelled_by} · {q.cancel_reason || q.cancel_result}</div>}
                    <div className="col-span-2 text-muted-foreground">Son sorgu: {fmtDT(q.last_synced)}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={openPreview} disabled={busy} data-testid="detail-preview-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}Önizle</Button>
                    <Button size="sm" variant="outline" onClick={openPdf} disabled={busy} data-testid="detail-pdf-btn"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
                    {onQnb && <Button size="sm" onClick={() => onQnb(inv)} data-testid="detail-qnb-btn"><Zap className="h-4 w-4 mr-1.5" />QNB İşlemleri (durum, tarihçe, iptal)</Button>}
                  </div>
                </>
              ) : inv.source_type === "efatura_gelen" ? (
                <div className="text-xs text-muted-foreground flex items-start gap-2"><Inbox className="h-4 w-4 shrink-0" /><span>QNB üzerinden gelen e-Fatura (ETTN {inv.qnb_ettn || "—"}). Belge görüntüsü ve yanıt bilgisi <b>Alış Faturaları → Gelen e-Faturalar</b> sekmesinde.</span></div>
              ) : (
                <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-2">
                  <span>{isSale ? "Bu fatura henüz QNB'den kesilmedi (taslak)." : "Manuel girilen alış faturası — QNB kaydı yok."}</span>
                  <Button size="sm" variant="outline" onClick={openPreview} disabled={busy} data-testid="detail-preview-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}Önizle</Button>
                  {isSale && onQnb && <Button size="sm" onClick={() => onQnb(inv)} data-testid="detail-qnb-btn"><Zap className="h-4 w-4 mr-1.5" />Faturayı Kes</Button>}
                </div>
              )}
            </Section>
          </div>

          <Section title={`Belgeler (${(inv.documents || []).length + (q.documents?.ubl ? 1 : 0)})`} testid="detail-docs">
            <div className="flex flex-wrap gap-2">
              {q.documents?.ubl && <Button size="sm" variant="outline" onClick={() => window.open(authUrl(q.documents.ubl), "_blank")} data-testid="detail-doc-ubl"><Paperclip className="h-3.5 w-3.5 mr-1.5" />UBL (XML)</Button>}
              {(inv.documents || []).map((d) => (
                <Button key={d.id} size="sm" variant="outline" onClick={() => (d.content_type || "").includes("pdf") || (d.content_type || "").startsWith("image") || (d.content_type || "").includes("html") ? setPreview({ url: d.url, title: d.name }) : window.open(authUrl(d.url), "_blank")} data-testid={`detail-doc-${d.id}`}>
                  <FileText className="h-3.5 w-3.5 mr-1.5" />{d.name}
                </Button>
              ))}
              {(inv.documents || []).length === 0 && !q.documents?.ubl && <span className="text-xs text-muted-foreground">Belge yok.</span>}
            </div>
          </Section>
        </div>
        <DialogFooter className="p-5 pt-0"><Button variant="outline" onClick={() => onClose && onClose()} data-testid="detail-close">Kapat</Button></DialogFooter>
        <HtmlPreviewDialog open={!!preview} title={preview?.title} url={preview?.url} html={preview?.html} regen={preview?.regen} onClose={() => setPreview(null)} />
      </DialogContent>
    </Dialog>
  );
}
